/* global gsap */

class HomepageLoader {
  constructor() {
    this.isDirectAccess = this.checkIfDirectAccess();
    this.loader = null;
    this.timeline = null;
    this.imageUrls = [];

    if (this.isDirectAccess && this.isHomepage()) {
      this.init();
    }
  }

  checkIfDirectAccess() {
    // Check if we're coming from a Barba navigation
    const wasNavigating = sessionStorage.getItem('pb_barba_navigating');
    if (wasNavigating) {
      return false; // This is a Barba navigation, don't show loader
    }
    
    // Check if Barba has been initialized and has navigation history
    if (typeof barba !== 'undefined' && barba.history && barba.history.length > 1) {
      return false; // This is a Barba navigation
    }
    
    // Check browser navigation type for direct access/refresh
    if (window.performance && window.performance.navigation) {
      const navType = window.performance.navigation.type;
      // TYPE_RELOAD = 1 (refresh), TYPE_NAVIGATE = 0 (direct access/bookmark/typing URL)
      if (navType === 1 || navType === 0) {
        return true; // Direct access or refresh - show loader
      }
    }
    
    // For modern browsers, use Navigation API if available
    if (window.performance && window.performance.getEntriesByType) {
      const navEntries = window.performance.getEntriesByType('navigation');
      if (navEntries.length > 0) {
        const navEntry = navEntries[0];
        // Show loader for reload and navigate types
        return navEntry.type === 'reload' || navEntry.type === 'navigate';
      }
    }
    
    // Default to true for direct access (covers edge cases)
    return true;
  }

  isHomepage() {
    return window.location.pathname === '/' || 
           window.location.pathname === '' || 
           document.body.classList.contains('homepage-loading');
  }

  async init() {
    await this.collectImages();
    
    if (this.imageUrls.length > 0) {
      this.createLoader();
      this.startAnimation();
    } else {
      this.showContent();
    }
  }

  async collectImages() {
    try {
      // Get products from multiple pages to ensure variety across collections
      const responses = await Promise.all([
        fetch('/products.json?limit=50'),
        fetch('/products.json?limit=50&page=2'),
        fetch('/products.json?limit=50&page=3')
      ]);
      
      const allData = await Promise.all(responses.map(r => r.ok ? r.json() : { products: [] }));
      const allProducts = allData.flatMap(data => data.products || []);
      
      // Shuffle products to mix collections
      const shuffledProducts = allProducts.sort(() => Math.random() - 0.5);
      
      // Get all available images and convert to smaller sizes
      const allImages = shuffledProducts
        .flatMap(product => product.images.map(img => this.getOptimizedImageUrl(img.src)))
        .filter(src => src && src.length > 0);
      
      // Take exactly 24 images, repeat if needed
      this.imageUrls = [];
      for (let i = 0; i < 24; i++) {
        if (allImages.length > 0) {
          this.imageUrls.push(allImages[i % allImages.length]);
        }
      }
        
    } catch (error) {
      this.imageUrls = [];
    }
  }

  getOptimizedImageUrl(url) {
    if (!url) return '';
    
    // For Shopify CDN images, add size parameter for faster loading
    if (url.includes('cdn.shopify.com')) {
      // Remove existing size parameters and add 400x250 (16:10 ratio)
      const cleanUrl = url.replace(/_\d+x\d*\./, '.').replace(/\?.*$/, '');
      return cleanUrl.replace(/\.(jpg|jpeg|png|webp|gif)$/i, '_700x550.$1');
    }
    
    return url;
  }

  createLoader() {
    const imagesToUse = this.imageUrls.slice(0, 24); // Always use 24 images for 6x4 grid
    
    this.loader = document.createElement('div');
    this.loader.className = 'homepage-loader';
    this.loader.innerHTML = `
      <div class="loader-grid">
        ${imagesToUse.map(url => `<img class="loader-image" src="${url}" alt="">`).join('')}
      </div>
      <div class="loader-logo">
        <img src="${this.getLogoUrl()}" alt="Paul Bride Photography">
      </div>
    `;
    
    document.body.appendChild(this.loader);
  }

  getLogoUrl() {
    const logo = document.querySelector('.logo img, header img');
    if (logo && logo.src) return logo.src;
    
    return 'data:image/svg+xml;base64,' + btoa(`
      <svg xmlns="http://www.w3.org/2000/svg" width="300" height="80" viewBox="0 0 300 80">
        <text x="150" y="45" text-anchor="middle" font-family="system-ui" font-size="28" fill="#000">
          PAUL BRIDE
        </text>
        <text x="150" y="65" text-anchor="middle" font-family="system-ui" font-size="12" fill="#666" letter-spacing="3px">
          PHOTOGRAPHY
        </text>
      </svg>
    `);
  }

  startAnimation() {
    const images = this.loader.querySelectorAll('.loader-image');
    const logo = this.loader.querySelector('.loader-logo');
    
    this.timeline = gsap.timeline({ onComplete: () => this.cleanup() });
    
    // Show images
    this.timeline.to(images, {
      opacity: 1,
      scale: 1,
      duration: 0.4,
      stagger: 0.05,
      ease: 'power2.out'
    });
    
    // Hide images, show logo
    this.timeline.to(images, { opacity: 0, duration: 0.6, stagger: 0.02 }, '+=0.5');
    this.timeline.to(logo, { opacity: 1, duration: 0.8 }, '-=0.3');
    
    // Hide everything and show content
    this.timeline.call(() => this.showContent(), null, '+=1');
    this.timeline.to(this.loader, { opacity: 0, duration: 0.8 });
  }

  showContent() {
    document.body.classList.remove('homepage-loading');
    document.body.classList.add('homepage-loaded');
    
    // Dispatch event to signal loader completion
    document.dispatchEvent(new CustomEvent('homepageLoaderComplete'));
    
    // Delay banner autoplay to prevent rapid switching after loader
    setTimeout(() => {
      this.enableBannerAutoplay();
    }, 1500); // 1.5 second delay after loader completes
  }
  
  enableBannerAutoplay() {
    // Find the home-banner section and enable autoplay
    const bannerSection = document.querySelector('.home-banner, [data-section-type="home-banner"]');
    if (bannerSection) {
      // Method 1: Dispatch custom event for banner to start autoplay
      bannerSection.dispatchEvent(new CustomEvent('startAutoplay'));
      
      // Method 2: If banner uses a specific class to control autoplay
      bannerSection.classList.add('autoplay-enabled');
      
      // Method 3: If banner has a data attribute to control autoplay
      bannerSection.dataset.autoplayEnabled = 'true';
      
      // Method 4: Trigger any existing autoplay initialization
      if (window.initializeBannerAutoplay) {
        window.initializeBannerAutoplay();
      }
    }
    
    // Also check for any Swiper instances and enable autoplay
    if (typeof Swiper !== 'undefined' && window.bannerSwiper) {
      window.bannerSwiper.autoplay.start();
    }
    
    // Check for any banner-specific autoplay functions
    if (window.startBannerAutoplay && typeof window.startBannerAutoplay === 'function') {
      window.startBannerAutoplay();
    }
  }

  cleanup() {
    setTimeout(() => {
      if (this.loader && this.loader.parentNode) {
        this.loader.remove();
      }
    }, 500);
  }
}

// Initialize
if (typeof gsap !== 'undefined') {
  // Set up Barba hooks to prevent loader on navigation
  if (typeof barba !== 'undefined') {
    // Before any Barba transition, mark that we're navigating
    barba.hooks.before(() => {
      sessionStorage.setItem('pb_barba_navigating', 'true');
    });
    
    // After entering a new page, check if we should skip the loader
    barba.hooks.after(() => {
      const wasNavigating = sessionStorage.getItem('pb_barba_navigating');
      
      // If we navigated to homepage via Barba, don't show loader but allow banner autoplay
      if (wasNavigating && window.location.pathname === '/') {
        document.body.classList.remove('homepage-loading');
        document.body.classList.add('homepage-loaded');
        
        // Allow banner autoplay to start normally since no loader is running
        setTimeout(() => {
          document.dispatchEvent(new CustomEvent('homepageLoaderComplete'));
        }, 100);
      }
      
      // Clean up navigation flag
      sessionStorage.removeItem('pb_barba_navigating');
    });
  }
  
  new HomepageLoader();
} else {
  setTimeout(() => new HomepageLoader(), 100);
}